/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors in child components and displays
 * a fallback UI instead of crashing the whole application.
 */

import { Component, ReactNode } from 'react';
import {
  Button,
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ErrorCircle24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: tokens.spacingVerticalXXL,
  },
  card: {
    maxWidth: '500px',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
  },
  icon: {
    color: tokens.colorPaletteRedForeground1,
    width: '48px',
    height: '48px',
  },
  details: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    overflow: 'auto',
    maxHeight: '150px',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
});

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Whether to show error details (development only) */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallback
        error={this.state.error}
        errorInfo={this.state.errorInfo}
        showDetails={this.props.showDetails ?? false}
        onRetry={this.handleRetry}
        onReload={this.handleReload}
      />;
    }

    return this.props.children;
  }
}

// Fallback UI component
interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails?: boolean;
  onRetry: () => void;
  onReload: () => void;
}

function ErrorFallback({ error, errorInfo, showDetails, onRetry, onReload }: ErrorFallbackProps) {
  const styles = useStyles();
  const isDev = (import.meta.env as ImportMetaEnv).DEV;

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          image={<ErrorCircle24Regular className={styles.icon} />}
          header={<Text weight="semibold" size={500}>Something went wrong</Text>}
          description="An unexpected error occurred. Please try again."
        />
        <div className={styles.content}>
          <Text>
            We apologize for the inconvenience. You can try refreshing the page
            or contact support if the problem persists.
          </Text>

          {(showDetails ?? isDev) && error && (
            <div className={styles.details}>
              <Text weight="semibold" block>Error: {error.message}</Text>
              {errorInfo?.componentStack && (
                <pre>{errorInfo.componentStack}</pre>
              )}
            </div>
          )}

          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={<ArrowClockwise24Regular />}
              onClick={onRetry}
            >
              Try Again
            </Button>
            <Button appearance="outline" onClick={onReload}>
              Reload Page
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
